import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Eye, Loader2, Pencil, Send, Square, Type, X } from 'lucide-react';

import type { AiChatSourceParticle } from '../utils/aiChatSourceParticles';
import {
  appendAiChatMessageStream,
  getAiChatThread,
  getOrCreateAiChatThread,
  listAiChatModels,
  type AiChatModelOption,
  type UserAiChatThread,
} from '../services/userAiChatService';
import { knowledgeService } from '../services/knowledgeService';
import type { KnowledgeParticle } from '../types/domain';
import {
  parseParticleQaPatchFromText,
  stripParticleQaPatchFence,
  type ParticleQaPatch,
} from '../utils/particleQaPatch';
import { MathTextBlock } from './MathTextBlock';
import { AiGeneratedInlineMark } from './AiGeneratedContentBadge';

export type AiChatContextBlock = {
  id: string;
  kind: 'text_segment' | 'note' | 'custom';
  particleId?: string;
  label?: string;
  text: string;
};

export type AiChatDialogPanelProps = {
  open: boolean;
  onClose: () => void;
  particleId?: string;
  particleTitle?: string;
  contextBlocks?: AiChatContextBlock[];
  sourceParticles?: AiChatSourceParticle[];
  entrypoint: 'ttqib-segment' | 'kp-detail' | 'kp-graph' | 'kp-error-feedback' | string;
  ttqaId?: string;
  surface?: 'ttq_session' | 'kp_detail' | string;
  subject?: string;
  threadId?: string | null;
  onThreadChange?: (threadId: string | null) => void;
  onParticleUpdated?: (particle: KnowledgeParticle) => void;
};

type ChatBubble = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  reasoning?: string | null;
};

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { name?: string; code?: string; message?: string };
  return err.name === 'AbortError'
    || err.code === 'ABORTED'
    || /aborted|abort|已停止/i.test(String(err.message || ''));
}

function isPersistedUserMessage(item: ChatBubble): boolean {
  return item.role === 'user' && !item.id.startsWith('local-');
}

function ModelBrandIcon({
  model,
  className = '',
  size = 14,
}: {
  model: Pick<AiChatModelOption, 'brandIconUrl' | 'icon' | 'family'> | null;
  className?: string;
  size?: number;
}) {
  if (model?.brandIconUrl) {
    return (
      <img
        src={model.brandIconUrl}
        alt={model.family || 'model'}
        width={size}
        height={size}
        className={`object-contain ${className}`}
        draggable={false}
      />
    );
  }
  if (model?.icon === 'vision' || model?.icon === 'qwen' || model?.icon === 'wanx') {
    return <Eye size={size} className={className} />;
  }
  return <Type size={size} className={className} />;
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

function particleDisplayTitle(particle: AiChatSourceParticle): string | null {
  const title = String(particle.title || '').trim();
  if (!title || looksLikeUuid(title)) return null;
  return title;
}

function ThinkingBlock({ reasoning, streaming = false }: { reasoning: string; streaming?: boolean }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-2 rounded-lg border border-border/80 bg-canvas/40">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-[11px] text-muted"
      >
        <span>{streaming ? '思考中…' : '思考过程'}</span>
        <ChevronDown size={12} className={open ? 'rotate-180 transition' : 'transition'} />
      </button>
      {open ? (
        <p className="max-h-40 overflow-y-auto whitespace-pre-wrap border-t border-border/60 px-2.5 py-2 text-[11px] leading-relaxed text-ink/75">
          {reasoning}
          {streaming ? <span className="ml-0.5 inline-block animate-pulse">▍</span> : null}
        </p>
      ) : null}
    </div>
  );
}

function ContextPanel({
  sources,
  blocks,
}: {
  sources: AiChatSourceParticle[];
  blocks: AiChatContextBlock[];
}) {
  const [expanded, setExpanded] = useState(false);
  if (!sources.length && !blocks.length) return null;

  const summaryParts: string[] = [];
  if (sources.length) summaryParts.push(`${sources.length} 题`);
  if (blocks.length) summaryParts.push(`${blocks.length} 选段`);
  const preview = blocks[0]?.text || sources[0]?.questionText || '';

  return (
    <div className="shrink-0 border-b border-border bg-canvas/30">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left"
      >
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted">上下文</span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-ink/70">
          {summaryParts.join(' · ')}
          {preview ? ` · ${preview}` : ''}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-muted transition ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded ? (
        <div className="max-h-40 space-y-2.5 overflow-y-auto border-t border-border/60 px-4 py-2">
          {sources.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-[10px] font-medium text-muted">来源题目</p>
              {sources.map((particle) => {
                const title = particleDisplayTitle(particle);
                return (
                  <div key={particle.particleId} className="space-y-0.5 text-xs text-ink/80">
                    {title ? (
                      <p className="font-medium text-primary">{title}</p>
                    ) : null}
                    <p className="line-clamp-3 leading-relaxed">{particle.questionText}</p>
                  </div>
                );
              })}
            </div>
          ) : null}
          {blocks.length > 0 ? (
            <div className="space-y-1">
              <p className="text-[10px] font-medium text-muted">已带入选段</p>
              {blocks.map((block, index) => (
                <p key={block.id} className="line-clamp-2 text-xs leading-relaxed text-ink/80">
                  <span className="mr-1 text-primary">{index + 1}.</span>
                  {block.label ? <span className="text-muted">[{block.label}] </span> : null}
                  {block.text}
                  <AiGeneratedInlineMark baseFontSize={12} />
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function contextFromThread(thread: UserAiChatThread | null | undefined): {
  blocks: AiChatContextBlock[];
  sources: AiChatSourceParticle[];
} {
  const ctx = (thread?.context || {}) as Record<string, unknown>;
  const rawSegments = Array.isArray(ctx.selectedSegments) ? ctx.selectedSegments : [];
  const blocks: AiChatContextBlock[] = rawSegments
    .map((item, index) => {
      const row = item as Record<string, unknown>;
      const text = String(row.text || '').trim();
      if (!text) return null;
      return {
        id: String(row.id || `seg-${index}`),
        kind: 'text_segment' as const,
        particleId: row.particleId ? String(row.particleId) : undefined,
        label: row.label ? String(row.label) : undefined,
        text,
      };
    })
    .filter(Boolean) as AiChatContextBlock[];

  const rawSources = Array.isArray(ctx.sourceParticles) ? ctx.sourceParticles : [];
  const sources: AiChatSourceParticle[] = rawSources
    .map((item) => {
      const row = item as Record<string, unknown>;
      const particleId = String(row.particleId || '').trim();
      const questionText = String(row.questionText || '').trim();
      if (!particleId || !questionText) return null;
      return {
        particleId,
        title: row.title ? String(row.title) : undefined,
        questionText,
        answerText: row.answerText ? String(row.answerText) : undefined,
      };
    })
    .filter(Boolean) as AiChatSourceParticle[];

  return { blocks, sources };
}

/**
 * 可复用 AI 对话浮层（用户级持久化，按选段锚点续聊）。
 */
export function AiChatDialogPanel({
  open,
  onClose,
  particleId,
  particleTitle,
  contextBlocks = [],
  sourceParticles = [],
  entrypoint,
  ttqaId,
  surface = 'ttq_session',
  subject,
  threadId: threadIdProp = null,
  onThreadChange,
  onParticleUpdated,
}: AiChatDialogPanelProps) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [title, setTitle] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(threadIdProp);
  const [messages, setMessages] = useState<ChatBubble[]>([]);
  const [resendFromMessageId, setResendFromMessageId] = useState<string | null>(null);
  const [models, setModels] = useState<AiChatModelOption[]>([]);
  const [modelKey, setModelKey] = useState('deepseek:v4-flash');
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [displayContextBlocks, setDisplayContextBlocks] = useState<AiChatContextBlock[]>(contextBlocks);
  const [displaySourceParticles, setDisplaySourceParticles] = useState<AiChatSourceParticle[]>(sourceParticles);
  const [applyingPatchMessageId, setApplyingPatchMessageId] = useState<string | null>(null);
  const [appliedPatchMessageIds, setAppliedPatchMessageIds] = useState<Record<string, true>>({});
  const listRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  const modelMenuRef = useRef<HTMLDivElement>(null);
  const bootKeyRef = useRef<string>('');
  const sendAbortRef = useRef<AbortController | null>(null);
  const editSnapshotRef = useRef<ChatBubble[] | null>(null);
  const isErrorFeedback = entrypoint === 'kp-error-feedback';

  const selectedModel = useMemo(
    () => models.find((item) => item.key === modelKey) || models[0] || null,
    [models, modelKey],
  );
  const showThinkingUi = Boolean(selectedModel?.supportsThinking);

  const modelsByModality = useMemo(() => {
    const text = models.filter((item) => item.modality === 'text');
    const multimodal = models.filter((item) => item.modality === 'multimodal');
    const image = models.filter((item) => item.modality === 'image');
    return { text, multimodal, image };
  }, [models]);

  useEffect(() => {
    setThreadId(threadIdProp);
  }, [threadIdProp]);

  useEffect(() => {
    if (!modelMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const node = modelMenuRef.current;
      if (!node || node.contains(event.target as Node)) return;
      setModelMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [modelMenuOpen]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await listAiChatModels();
        if (cancelled) return;
        setModels(result.models);
        setModelKey((current) => {
          if (result.models.some((item) => item.key === current)) return current;
          return result.defaultModelKey || result.models[0]?.key || 'deepseek:v4-flash';
        });
      } catch {
        if (!cancelled) {
          setModels([
            {
              key: 'deepseek:v4-flash',
              family: 'DeepSeek',
              version: 'deepseek-v4-flash',
              label: 'DeepSeek V4 Flash',
              modality: 'text',
              mode: 'chat',
              icon: 'deepseek',
              brandIconUrl: 'https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/light/deepseek-color.png',
              supportsThinking: false,
              selectable: true,
            },
            {
              key: 'deepseek:v4-pro',
              family: 'DeepSeek',
              version: 'deepseek-v4-pro',
              label: 'DeepSeek V4 Pro',
              modality: 'text',
              mode: 'reasoner',
              icon: 'deepseek',
              brandIconUrl: 'https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/light/deepseek-color.png',
              supportsThinking: true,
              selectable: true,
            },
            {
              key: 'qwen:3.7-max',
              family: 'Qwen',
              version: 'qwen3.7-max',
              label: 'Qwen 3.7 Max',
              modality: 'text',
              mode: 'reasoner',
              icon: 'qwen',
              brandIconUrl: 'https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/light/qwen-color.png',
              supportsThinking: true,
              selectable: true,
            },
            {
              key: 'qwen:3.7-plus',
              family: 'Qwen',
              version: 'qwen3.7-plus',
              label: 'Qwen 3.7 Plus',
              modality: 'multimodal',
              mode: 'chat',
              icon: 'qwen',
              brandIconUrl: 'https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/light/qwen-color.png',
              supportsThinking: false,
              selectable: true,
            },
            {
              key: 'wanx:2.7-image',
              family: '万相',
              version: 'wan2.7-image',
              label: '万相 2.1 Turbo',
              modality: 'image',
              mode: 'chat',
              icon: 'wanx',
              brandIconUrl: 'https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/light/qwen-color.png',
              supportsThinking: false,
              selectable: true,
            },
          ]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      sendAbortRef.current?.abort();
      sendAbortRef.current = null;
      setDraft('');
      setBusy(false);
      setBootstrapping(false);
      setError(null);
      setLimitReached(false);
      setMessages([]);
      setTitle(null);
      setResendFromMessageId(null);
      setModelMenuOpen(false);
      setDisplayContextBlocks(contextBlocks);
      setDisplaySourceParticles(sourceParticles);
      setApplyingPatchMessageId(null);
      setAppliedPatchMessageIds({});
      editSnapshotRef.current = null;
      bootKeyRef.current = '';
      return;
    }

    setDisplayContextBlocks(contextBlocks);
    setDisplaySourceParticles(sourceParticles);

    const selectedSegments = contextBlocks
      .map((block) => ({
        id: block.id,
        particleId: block.particleId,
        text: block.text,
        label: block.label,
      }))
      .filter((item) => item.text.trim());

    if (!selectedSegments.length && !threadIdProp) {
      setError('请先选择文本段再发起对话');
      return;
    }

    const bootKey = threadIdProp
      ? `id:${threadIdProp}`
      : `ep:${entrypoint}|seg:${selectedSegments.map((item) => `${item.particleId}:${item.text}`).join('|')}`;
    if (bootKeyRef.current === bootKey) return;
    bootKeyRef.current = bootKey;

    let cancelled = false;
    setBootstrapping(true);
    setError(null);
    void (async () => {
      try {
        const applyThreadContext = (thread: UserAiChatThread) => {
          const fromThread = contextFromThread(thread);
          if (fromThread.blocks.length) setDisplayContextBlocks(fromThread.blocks);
          if (fromThread.sources.length) setDisplaySourceParticles(fromThread.sources);
        };

        if (threadIdProp) {
          const result = await getAiChatThread(threadIdProp);
          if (cancelled) return;
          setThreadId(result.thread.id);
          onThreadChange?.(result.thread.id);
          setTitle(result.thread.title);
          setLimitReached(result.thread.contextByteSize >= result.contextByteLimit);
          applyThreadContext(result.thread);
          setMessages((result.messages || []).map((item) => ({
            id: item.id,
            role: item.role,
            content: item.content,
            reasoning: item.reasoning,
          })));
          return;
        }

        const result = await getOrCreateAiChatThread({
          entrypoint,
          surface,
          ttqaId,
          primaryParticleId: particleId,
          particleIds: [
            ...new Set([
              particleId,
              ...selectedSegments.map((item) => item.particleId),
            ].filter(Boolean) as string[]),
          ],
          selectedSegments,
          sourceParticles,
          deepLink: {
            surface,
            ttqaId,
            particleId,
            subject,
          },
        });
        if (cancelled) return;
        setThreadId(result.thread.id);
        onThreadChange?.(result.thread.id);
        setTitle(result.thread.title);
        setLimitReached(result.thread.contextByteSize >= result.contextByteLimit);
        applyThreadContext(result.thread);
        setMessages((result.messages || []).map((item) => ({
          id: item.id,
          role: item.role,
          content: item.content,
          reasoning: item.reasoning,
        })));
      } catch (bootError) {
        if (cancelled) return;
        setError(bootError instanceof Error ? bootError.message : '打开对话失败');
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contextBlocks, particleId, entrypoint, surface, ttqaId, threadIdProp]);

  useEffect(() => {
    const node = listRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [messages, busy, bootstrapping]);

  useEffect(() => {
    const node = draftRef.current;
    if (!node) return;
    node.style.height = 'auto';
    const next = Math.min(Math.max(node.scrollHeight, 44), 200);
    node.style.height = `${next}px`;
  }, [draft, open, resendFromMessageId]);

  const handleStop = () => {
    sendAbortRef.current?.abort();
    sendAbortRef.current = null;
  };

  const handleCancelEdit = () => {
    if (editSnapshotRef.current) {
      setMessages(editSnapshotRef.current);
    }
    editSnapshotRef.current = null;
    setResendFromMessageId(null);
    setDraft('');
    setError(null);
  };

  const handleStartEdit = (item: ChatBubble) => {
    if (busy || !isPersistedUserMessage(item)) return;
    const index = messages.findIndex((msg) => msg.id === item.id);
    if (index < 0) return;
    if (!editSnapshotRef.current) {
      editSnapshotRef.current = messages;
    }
    setResendFromMessageId(item.id);
    setDraft(item.content);
    setMessages(messages.slice(0, index));
    setError(null);
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || busy || !threadId || limitReached) return;
    const replaceFrom = resendFromMessageId;
    const streamId = `local-a-${Date.now()}`;
    setDraft('');
    setError(null);
    setModelMenuOpen(false);
    setMessages((prev) => [
      ...prev,
      { id: `local-u-${Date.now()}`, role: 'user', content: text },
      { id: streamId, role: 'assistant', content: '', reasoning: showThinkingUi ? '' : null },
    ]);
    setBusy(true);
    const controller = new AbortController();
    sendAbortRef.current = controller;
    try {
      const result = await appendAiChatMessageStream({
        threadId,
        message: text,
        modelKey,
        signal: controller.signal,
        replaceFromMessageId: replaceFrom || undefined,
        onReasoningDelta: showThinkingUi
          ? (delta) => {
            setMessages((prev) => prev.map((item) => (
              item.id === streamId
                ? { ...item, reasoning: `${item.reasoning || ''}${delta}` }
                : item
            )));
          }
          : undefined,
        onContentDelta: (delta) => {
          setMessages((prev) => prev.map((item) => (
            item.id === streamId
              ? { ...item, content: `${item.content || ''}${delta}` }
              : item
          )));
        },
      });
      setMessages((result.messages || []).map((item) => ({
        id: item.id,
        role: item.role,
        content: item.content,
        reasoning: item.reasoning,
      })));
      setTitle(result.title || result.thread.title);
      setLimitReached(result.thread.contextByteSize >= result.contextByteLimit);
      setResendFromMessageId(null);
      editSnapshotRef.current = null;
    } catch (sendError) {
      setMessages((prev) => prev.filter((item) => !item.id.startsWith('local-')));
      setDraft(text);
      if (replaceFrom) {
        setResendFromMessageId(replaceFrom);
      }
      if (isAbortError(sendError)) {
        setError(null);
      } else {
        const err = sendError as Error & { code?: string };
        setError(err.message || '发送失败');
        if (err.code === 'CONTEXT_LIMIT') setLimitReached(true);
      }
    } finally {
      if (sendAbortRef.current === controller) {
        sendAbortRef.current = null;
      }
      setBusy(false);
    }
  };

  if (!open) return null;

  const headerTitle = title
    || (isErrorFeedback
      ? (particleTitle ? `反馈报错 · ${particleTitle}` : '反馈报错')
      : (particleTitle ? `对话 · ${particleTitle}` : '知识颗粒对话'));

  const handleApplyPatch = async (messageId: string, patch: ParticleQaPatch) => {
    const targetParticleId = String(patch.particleId || particleId || '').trim();
    if (!targetParticleId) {
      setError('缺少知识颗粒 ID，无法写回');
      return;
    }
    setApplyingPatchMessageId(messageId);
    setError(null);
    try {
      const updated = await knowledgeService.applyKnowledgeParticleQaPatch(targetParticleId, {
        changeScope: patch.changeScope,
        summary: patch.summary,
        questions: patch.questions,
        answers: patch.answers,
        threadId: threadId || undefined,
      });
      setAppliedPatchMessageIds((prev) => ({ ...prev, [messageId]: true }));
      onParticleUpdated?.(updated);
    } catch (applyError) {
      setError(applyError instanceof Error ? applyError.message : '写回失败');
    } finally {
      setApplyingPatchMessageId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/25 p-3 sm:items-center">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="关闭对话"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-label="知识颗粒对话"
        className="relative z-[61] flex max-h-[min(720px,86vh)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">{headerTitle}</p>
            <p className="text-[11px] text-muted">
              {limitReached ? '上下文已达 1MB 上限，暂停继续发送' : '对话已保存，可随时从学习菜单继续'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-black/[0.05]"
            aria-label="关闭"
          >
            <X size={16} />
          </button>
        </div>

        <ContextPanel sources={displaySourceParticles} blocks={displayContextBlocks} />

        <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {bootstrapping ? (
            <div className="inline-flex items-center gap-2 text-xs text-muted">
              <Loader2 size={14} className="animate-spin" />
              加载对话…
            </div>
          ) : null}
          {!bootstrapping && messages.length === 0 ? (
            <p className="text-sm text-muted">
              {isErrorFeedback
                ? '说明哪里错了，或直接发送让 AI 对照选段诊断；确认后可写回知识颗粒。'
                : '可以直接提问选中文本；同一选段会续聊已有对话。'}
            </p>
          ) : null}
          {messages.map((item) => {
            const isStreamingAssistant = busy && item.id.startsWith('local-a-');
            const reasoningText = String(item.reasoning || '').trim();
            const showReasoning = showThinkingUi && (reasoningText.length > 0 || isStreamingAssistant);
            const patch = item.role === 'assistant' && !isStreamingAssistant
              ? parseParticleQaPatchFromText(item.content)
              : null;
            const displayContent = item.role === 'assistant'
              ? (stripParticleQaPatchFence(item.content) || (patch ? patch.summary : item.content))
              : item.content;
            const patchApplied = Boolean(appliedPatchMessageIds[item.id]);
            const patchApplying = applyingPatchMessageId === item.id;
            return (
              <div
                key={item.id}
                className={
                  item.role === 'user'
                    ? 'relative ml-8 rounded-xl bg-primary/10 px-3 py-2 pb-7 text-sm text-ink'
                    : 'mr-8 rounded-xl border border-border bg-canvas/50 px-3 py-2 text-sm text-ink'
                }
              >
                {item.role === 'assistant' && showReasoning ? (
                  <ThinkingBlock
                    reasoning={reasoningText || (isStreamingAssistant ? '…' : '')}
                    streaming={isStreamingAssistant && !item.content}
                  />
                ) : null}
                {item.role === 'assistant' ? (
                  displayContent ? (
                    <div className="ai-chat-message-body">
                      <div className="ai-chat-assistant-body">
                        <MathTextBlock text={displayContent} className="leading-relaxed" />
                        <AiGeneratedInlineMark baseFontSize={14} />
                      </div>
                    </div>
                  ) : isStreamingAssistant ? (
                    <p className="text-xs text-muted">
                      {showThinkingUi && !reasoningText ? '正在思考…' : '…'}
                    </p>
                  ) : null
                ) : (
                  <p className="whitespace-pre-wrap leading-relaxed">{item.content}</p>
                )}
                {patch ? (
                  <div className="mt-2 space-y-2 rounded-lg border border-border bg-canvas/80 p-2.5">
                    <p className="text-xs font-semibold text-ink">修订草案</p>
                    <p className="text-[11px] leading-relaxed text-muted">
                      {patch.summary}
                      {' · '}
                      {patch.changeScope === 'question_and_answer'
                        ? '题干+答案'
                        : patch.changeScope === 'format_only'
                          ? '仅格式'
                          : '仅答案'}
                    </p>
                    <button
                      type="button"
                      disabled={busy || patchApplied || patchApplying}
                      onClick={() => { void handleApplyPatch(item.id, patch); }}
                      className="inline-flex w-full items-center justify-center rounded-md bg-primary px-2 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {patchApplying ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : patchApplied ? '已写回' : '确认写回知识颗粒'}
                    </button>
                  </div>
                ) : null}
                {isPersistedUserMessage(item) && !busy ? (
                  <button
                    type="button"
                    onClick={() => handleStartEdit(item)}
                    className="absolute bottom-1.5 right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted hover:bg-black/[0.06] hover:text-primary"
                    aria-label="编辑后再次发送"
                    title="编辑后再次发送"
                  >
                    <Pencil size={12} />
                  </button>
                ) : null}
              </div>
            );
          })}
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>

        <div className="border-t border-border p-3">
          {resendFromMessageId ? (
            <div className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-canvas/50 px-2.5 py-1.5 text-[11px] text-muted">
              <span>编辑重发：将清除该条及之后的对话</span>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={busy}
                className="shrink-0 text-primary disabled:opacity-50"
              >
                取消
              </button>
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            <div ref={modelMenuRef} className="relative shrink-0">
              <button
                type="button"
                disabled={busy || bootstrapping}
                onClick={() => setModelMenuOpen((value) => !value)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-canvas/40 text-ink disabled:opacity-50"
                aria-label={selectedModel ? `当前模型 ${selectedModel.family}` : '选择模型'}
                title={selectedModel ? `${selectedModel.family} · ${selectedModel.version}` : '选择模型'}
              >
                {selectedModel ? (
                  <ModelBrandIcon model={selectedModel} size={16} />
                ) : (
                  <Type size={14} />
                )}
              </button>
              {modelMenuOpen ? (
                <div className="absolute bottom-12 left-0 z-20 w-60 overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
                  {modelsByModality.text.length ? (
                    <div className="border-b border-border px-2 py-1.5">
                      <p className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted">文本模型</p>
                      {modelsByModality.text.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          disabled={!item.selectable}
                          onClick={() => {
                            setModelKey(item.key);
                            setModelMenuOpen(false);
                          }}
                          className={[
                            'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs',
                            item.key === modelKey ? 'bg-primary/10 text-primary' : 'text-ink hover:bg-black/[0.04]',
                            item.selectable ? '' : 'opacity-50',
                          ].join(' ')}
                        >
                          <ModelBrandIcon model={item} size={16} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{item.label || item.family}</span>
                            <span className="block truncate text-[10px] text-muted">{item.version}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {modelsByModality.multimodal.length ? (
                    <div className="border-b border-border px-2 py-1.5">
                      <p className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted">多模态模型</p>
                      {modelsByModality.multimodal.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          disabled={!item.selectable}
                          onClick={() => {
                            setModelKey(item.key);
                            setModelMenuOpen(false);
                          }}
                          className={[
                            'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs',
                            item.key === modelKey ? 'bg-primary/10 text-primary' : 'text-ink hover:bg-black/[0.04]',
                            item.selectable ? '' : 'opacity-50',
                          ].join(' ')}
                        >
                          <ModelBrandIcon model={item} size={16} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{item.label || item.family}</span>
                            <span className="block truncate text-[10px] text-muted">{item.version}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {modelsByModality.image.length ? (
                    <div className="px-2 py-1.5">
                      <p className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted">生图模型</p>
                      {modelsByModality.image.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          disabled={!item.selectable}
                          onClick={() => {
                            if (!item.selectable) return;
                            setModelKey(item.key);
                            setModelMenuOpen(false);
                          }}
                          className={[
                            'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs',
                            item.key === modelKey ? 'bg-primary/10 text-primary' : 'text-ink hover:bg-black/[0.04]',
                            item.selectable ? '' : 'cursor-default opacity-70',
                          ].join(' ')}
                          title={item.selectable ? undefined : '生图模型用于配图，对话请选择文本/多模态'}
                        >
                          <ModelBrandIcon model={item} size={16} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{item.label || item.family}</span>
                            <span className="block truncate text-[10px] text-muted">{item.version}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <textarea
              ref={draftRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={1}
              disabled={limitReached || bootstrapping || !threadId || busy}
              placeholder={
                limitReached
                  ? '上下文已达上限'
                  : resendFromMessageId
                    ? '修改后再次发送…'
                    : '输入问题…'
              }
              className="max-h-[200px] min-h-[44px] flex-1 resize-none overflow-y-auto rounded-lg border border-border bg-canvas/40 px-3 py-2 text-sm text-ink outline-none focus:border-primary disabled:opacity-50"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (!busy) void handleSend();
                }
              }}
            />
            {busy ? (
              <button
                type="button"
                onClick={handleStop}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ink text-white"
                aria-label="停止生成"
                title="停止生成"
              >
                <Square size={14} fill="currentColor" />
              </button>
            ) : (
              <button
                type="button"
                disabled={bootstrapping || !draft.trim() || !threadId || limitReached}
                onClick={() => { void handleSend(); }}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-white disabled:opacity-50"
                aria-label={resendFromMessageId ? '编辑后再次发送' : '发送'}
              >
                <Send size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
